/**
 * Future Probability™ — Layer 5.
 *
 * Five living indicators, each computed from named components with fixed
 * weights. Rules:
 *  - A component with no evidence yet scores neutral 50 and is FLAGGED.
 *    Never zero — absence of evidence is not evidence of absence.
 *  - Indicator confidence = share of weight actually backed by evidence.
 *  - Every indicator carries an explanation. Never a raw score alone.
 *  - Pure and deterministic: same evidence + same signals → same numbers.
 */

import { domainStrength } from "./infer";
import type {
  InferredSkill,
  LivingSignals,
  ProbabilityComponent,
  ProbabilityIndicator,
} from "./types";

const NEUTRAL = 50;

function comp(
  key: string,
  label: string,
  weight: number,
  value: number | null,
): ProbabilityComponent {
  return { key, label, weight, value, flagged: value == null };
}

/** Scale a count into 0–100 with a soft ceiling at `full`. */
function fromCount(n: number | null | undefined, full: number): number | null {
  if (n == null) return null;
  return Math.round(Math.min(100, (n / full) * 100));
}

function fromRatio(r: number | null | undefined): number | null {
  if (r == null) return null;
  return Math.round(Math.max(0, Math.min(1, r)) * 100);
}

function assemble(
  key: ProbabilityIndicator["key"],
  label: string,
  components: ProbabilityComponent[],
  explain: (value: number, backed: ProbabilityComponent[], missing: ProbabilityComponent[]) => string,
): ProbabilityIndicator {
  const totalWeight = components.reduce((t, c) => t + c.weight, 0) || 1;
  const value = Math.round(
    components.reduce((t, c) => t + (c.value ?? NEUTRAL) * (c.weight / totalWeight), 0),
  );
  const backedWeight = components
    .filter((c) => !c.flagged)
    .reduce((t, c) => t + c.weight, 0);
  const confidence = Math.round((backedWeight / totalWeight) * 100) / 100;
  const backed = components.filter((c) => !c.flagged).sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  const missing = components.filter((c) => c.flagged);
  return {
    key,
    label,
    value: Math.max(0, Math.min(99, value)),
    confidence,
    components,
    explanation: explain(value, backed, missing),
  };
}

function listLabels(cs: ProbabilityComponent[], n = 2): string {
  const l = cs.slice(0, n).map((c) => c.label.toLowerCase());
  if (l.length === 0) return "";
  if (l.length === 1) return l[0];
  return `${l.slice(0, -1).join(", ")} and ${l[l.length - 1]}`;
}

export function computeFutureProbability(
  skills: InferredSkill[],
  signals: LivingSignals = {},
): ProbabilityIndicator[] {
  const s = (ids: string[]) => domainStrength(skills, ids);

  const careerReadiness = assemble(
    "career_readiness",
    "Career Readiness",
    [
      comp("academic", "Learning progress", 0.30, fromRatio(signals.action_completion)),
      comp("portfolio", "Portfolio", 0.25, fromCount(signals.artefacts, 4)),
      comp("projects", "Projects finished", 0.20, s(["project_management", "programming", "making_hardware", "content_creation"])),
      comp("soft_skills", "People skills", 0.15, s(["collaboration", "listening", "writing", "customer_service"])),
      comp("interview", "Speaking for yourself", 0.10, s(["public_speaking", "storytelling", "influence"])),
    ],
    (v, backed, missing) =>
      missing.length === 0
        ? `${v}% — your strongest signals are ${listLabels(backed)}.`
        : `${v}% so far, resting on ${listLabels(backed) || "very little yet"}. Add ${listLabels(missing)} and this moves.`,
  );

  const purposeAlignment = assemble(
    "purpose_alignment",
    "Purpose Alignment",
    [
      comp("purpose_dna", "Purpose DNA", 0.35, signals.purpose_confidence ?? null),
      comp("motivation", "Momentum", 0.20, fromCount(signals.streak_days, 21)),
      comp("dreams", "Dreams pinned", 0.20, fromCount(signals.dreams, 5)),
      comp("interests", "Interests explored", 0.15, s(["curiosity", "research", "self_directed_learning"])),
      comp("values", "Values in action", 0.10, s(["community_organising", "caregiving", "teaching", "ai_ethics"])),
    ],
    (v, backed, missing) =>
      `${v}% — ${backed.length ? `driven by ${listLabels(backed)}` : "not much to go on yet"}${missing.length ? `; ${listLabels(missing)} still blank` : ""}.`,
  );

  const leadership = assemble(
    "leadership",
    "Leadership",
    [
      comp("communication", "Communication", 0.22, s(["public_speaking", "writing", "storytelling", "listening"])),
      comp("influence", "Influence", 0.20, s(["influence", "selling", "marketing"])),
      comp("initiative", "Initiative", 0.20, s(["initiative", "entrepreneurship"])),
      comp("decision", "Decision making", 0.20, s(["decision_making", "project_management", "delegation"])),
      comp("collaboration", "Collaboration", 0.18, s(["collaboration", "facilitation", "conflict_resolution", "mentoring"])),
    ],
    (v, backed, missing) =>
      `${v}% — ${backed.length ? `${listLabels(backed)} carry this` : "no leadership evidence yet"}${missing.length ? `; nothing recorded for ${listLabels(missing)}` : ""}.`,
  );

  const aiReadiness = assemble(
    "ai_readiness",
    "AI Readiness",
    [
      comp("prompting", "Prompting", 0.22, s(["prompting"])),
      comp("automation", "Automation", 0.22, s(["ai_automation"])),
      comp("literacy", "AI literacy", 0.20, s(["ai_tool_usage", "ml_foundations"])),
      comp("workflow", "Workflow design", 0.18, s(["workflow_design", "systems_thinking"])),
      comp("judgement", "AI judgement", 0.18, s(["ai_ethics"])),
    ],
    (v, backed, missing) =>
      missing.length >= 3
        ? `${v}% — mostly unmeasured. One AI project would tell us far more than another questionnaire.`
        : `${v}% — ${listLabels(backed)} are real; ${listLabels(missing) || "nothing"} still to prove.`,
  );

  const futureReadiness = assemble(
    "future_readiness",
    "Future Readiness",
    [
      comp("adaptability", "Adaptability", 0.22, s(["adaptability"])),
      comp("curiosity", "Curiosity", 0.20, s(["curiosity", "research"])),
      comp("velocity", "Learning velocity", 0.22, fromCount(signals.learning_events_90d, 6)),
      comp("systems", "Systems thinking", 0.18, s(["systems_thinking"])),
      comp("digital", "Digital fluency", 0.18, s(["digital_literacy", "programming", "spreadsheets", "cloud_deployment"])),
    ],
    (v, backed, missing) =>
      `${v}% — ${backed.length ? `${listLabels(backed)} are showing` : "nothing measured yet"}${missing.length ? `; ${listLabels(missing)} would raise it fastest` : ""}.`,
  );

  return [careerReadiness, purposeAlignment, leadership, aiReadiness, futureReadiness];
}
